import React, { useCallback, useEffect } from 'react'
import Areas from './areas'
import { useDispatch, useSelector } from 'react-redux'
import { fetchRestaurantAreasThunk, fetchRestaurantTablesThunk } from '../../redux/thunk/restaurant/table'
import RestaurantOrders from './orders'
import Tables from './areas/tables'
import Header from '../Header'
import MainWrapper from '../MainWrapper'
import { AreasMobileActions, AreasMobileHeading, AreasMobileWrapper } from './index.styled'
import { getTableOrderPrice, roundPrice, uuidGenerator } from '../../helper'
import { theme } from '../../style/theme'
import ModalTable from './areas/tables/modalTable'
import ModalPayment from './areas/tables/modalPayment'
import ModalOrder from './areas/tables/modalOrder'
import ModalTableSwap from './areas/tables/modalTableSwap'
import ModalSplitBoard from './areas/tables/modalSplitBoard'
import ModalOrderSplit from './areas/tables/modalOrderSplit'
import ModalPaymentType from './areas/tables/modalPaymentType'
import { closeTableOrdersThunk } from '../../redux/thunk/restaurant/order'
import { setRestaurantModalChangeCalculatorIsNeeded, setRestaurantTable } from '../../redux/action/restaurant'
import { useWindowBreakPoint } from '../../hooks/useWindowBreakPoint'
import { getRestaurantModalChangeCalculatorIsNeededThunk } from '../../redux/thunk/restaurant/changeCalculator'
import { getRestaurantSettingsDishCoursesThunk, getRestaurantSettingsPresetDiscountsThunk, getRestaurantSettingsWaiterSettingsThunk, getTableTimerConfigsThunk } from '../../redux/thunk/restaurantSettings'
import { getGiftCouponsPayment, getGiftCouponsSum } from '../../helper/getGiftCouponsPayment'
import ModalTableClosed from './areas/tables/ModalTableClosed'
import { selectDiscountOnTable, selectGiftCouponInOrders, selectProcessedBy } from '../../redux/selector/restaurant'
import PrintReceiptConfirmationModal from '../Elements/modal/PrintReceiptConfirmationModal'
import { convert2RestaurantCustomer } from './areas/tables/modalPaymentType/helper'
import { ROOM_CHARGE_PAYMENT_METHOD } from '../RoomCharge/constants'
import { resetCustomerThunk } from '../../redux/thunk/customer'
import ModalProcessPaymentError from './areas/tables/ModalProcessPaymentError'
import { selectCompanyIndex, selectHasAnyGroup } from '../../redux/selector/system'
import { setCartPaymentDeposits, setCartPaymentGiftCoupons } from '../../redux/action/cartPayment'
import SendToKitchenModal from './SendToKitchenModal'
import ModalTableWaiterNotified from './areas/tables/ModalTableWaiterNotified'
import { getKitchenConfigThunk } from '../../redux/thunk/kitchenMonitor'
import PaidBeforeOrdersModal from './areas/tables/PaidBeforeOrdersModal'
import { getDepositsSum } from '../../helper/getDepositsPayment'
import Bell from './Bell'
import WatchIcon from '../WatchConnection/WatchIcon'
import WatchPairingDrawer from '../WatchConnection/WatchPairingDrawer'
import { logger } from '../../logger/logger'

const Restaurant = () => {
	const selectedTable = useSelector(state => state.restaurant.table)
	const showSplitModal = useSelector(state => state.restaurant.showSplitModal)
	const areaId = useSelector(state => state.restaurant.areaId)

	const waiterAccessToken = useSelector(state => state.modalLogin.accessToken)
	const hasGiftCouponInOrders = useSelector(selectGiftCouponInOrders)
	const totalDiscount = useSelector(selectDiscountOnTable(selectedTable?.orders, hasGiftCouponInOrders))
	const isMobile = useWindowBreakPoint(theme.point720)
	const userIsWaiter = useSelector(selectHasAnyGroup(['WAITER']))
	const showWatchPairing = userIsWaiter
	const dispatch = useDispatch()
	const showPaidBeforeOrdersModal = useSelector(state => state.restaurant.showPaidBeforeOrdersModal)
	const hasModalLogin = !!waiterAccessToken
	const processedBy = useSelector(selectProcessedBy)
	const companyIndex = useSelector(selectCompanyIndex)

	useEffect(() => {
		dispatch(fetchRestaurantAreasThunk())
		dispatch(getRestaurantSettingsWaiterSettingsThunk('RESTAURANT'))
		dispatch(getRestaurantSettingsPresetDiscountsThunk())
		dispatch(resetCustomerThunk())
		dispatch(getRestaurantSettingsDishCoursesThunk())
		dispatch(getKitchenConfigThunk())
		dispatch(getTableTimerConfigsThunk())
		//eslint-disable-next-line
	}, [])

	useEffect(() => {
		dispatch(getRestaurantModalChangeCalculatorIsNeededThunk())

		return () => dispatch(setRestaurantModalChangeCalculatorIsNeeded(false))
		//eslint-disable-next-line
	}, [])

	const resetTable = useCallback(() => {
		dispatch(fetchRestaurantTablesThunk(areaId))
		dispatch(setRestaurantTable(null))
		dispatch(setCartPaymentGiftCoupons([]))
		dispatch(setCartPaymentDeposits([]))
	}, [dispatch, areaId])

	const handlePaymentType = useCallback(async ({ paymentId, paymentMethod, totalDiscount, extraDiscountItem, marketingCouponItem, tips = 0, totalPrice, giftCoupons = [], deposits = [], customer }) => {
		const isEcTerminalPaymentMethod = paymentMethod === 'EC_TERMINAL'
		const isTap2PayPaymentMethod = paymentMethod === 'TAP_TO_PAY'

		const giftCouponsPayment = getGiftCouponsPayment(giftCoupons)
		const giftCouponsSum = getGiftCouponsSum(giftCoupons)
		const depositsSum = getDepositsSum(deposits)
		const restSum = roundPrice(totalPrice - depositsSum - giftCouponsSum)
		const singlePayment = paymentMethod ? { id: paymentId, method: paymentMethod, total: restSum, payed: false } : null
		const payments = singlePayment ?
			[...deposits, ...giftCouponsPayment, singlePayment] :
			[...deposits, ...giftCouponsPayment]

		const extraDiscount = extraDiscountItem.itemId ? extraDiscountItem : null
		const customerData = customer ? convert2RestaurantCustomer(customer) : null
		const orderData = { paymentId, paymentMethod, totalDiscount, extraDiscount: extraDiscountItem.value, extraDiscountItem: extraDiscount, totalPrice, payments, tips, processedBy, customer: customerData, marketingCouponItem, printerMetaData: { userPrintRequest: { userName: processedBy } } }
		logger.info(`Close table ${selectedTable?.id} on total price ${totalPrice} companyIndex=${companyIndex} from deviceType: ${navigator.userAgent} and deviceId ${uuidGenerator()}`)
		const orderId = await dispatch(closeTableOrdersThunk(selectedTable.id, orderData, hasModalLogin))
		if (!isEcTerminalPaymentMethod && !isTap2PayPaymentMethod && paymentMethod !== ROOM_CHARGE_PAYMENT_METHOD) {
			resetTable()
		}
		return orderId
	}, [selectedTable?.id, hasModalLogin, dispatch, processedBy, resetTable, companyIndex])

	return (
		<>
			<Header right={<Bell />}>
				{!isMobile && <>
					{showWatchPairing && <WatchIcon />}
					<RestaurantOrders />
				</>}
			</Header>
			<MainWrapper className={isMobile ? 'restaurant-mob ' : ''}>
				{isMobile ?
					<>
						<AreasMobileActions>
							{showWatchPairing && <WatchIcon />}
							<RestaurantOrders />
						</AreasMobileActions>
						<AreasMobileHeading>Halle auswählen</AreasMobileHeading>
						<AreasMobileWrapper>
							<Areas>
								<Tables />
							</Areas>
						</AreasMobileWrapper>
					</> :
					<Tables />}
				<ModalTable />
				<ModalPayment />
				<ModalOrder />
				<ModalTableSwap />
				<ModalSplitBoard />
				<ModalOrderSplit />
				<ModalTableClosed />
				<SendToKitchenModal />
				{selectedTable && !showSplitModal && !showPaidBeforeOrdersModal &&
					<ModalPaymentType title={'Tisch #' + (selectedTable?.number || '')}
														totalPrice={getTableOrderPrice(selectedTable) || null}
														totalDiscount={totalDiscount}
														handlePaymentType={handlePaymentType}
														resetTable={resetTable}
														isShowOrderViewButton={true} />}
				<PrintReceiptConfirmationModal />
				<ModalProcessPaymentError />
				<ModalTableWaiterNotified />
				{showPaidBeforeOrdersModal && <PaidBeforeOrdersModal />}
			</MainWrapper>
			{showWatchPairing && <WatchPairingDrawer />}
		</>
	)
}

Restaurant.displayName = 'Restaurant'
export default Restaurant
